import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { BookingForm } from "./components/BookingForm";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { TrackingScripts } from "./components/TrackingScripts";
import { SEOHead } from "./components/SEOHead";
import ContactPage from "./pages/ContactPage";
import FleetPage from "./pages/FleetPage";
import AirportTransfersPage from "./pages/AirportTransfersPage";
import CorporatePage from "./pages/CorporatePage";
import EventsPage from "./pages/EventsPage";
import AdminPage from "./pages/AdminPage";
import { BlogList, BlogPost } from "./pages/BlogPage";
import { 
  Phone, 
  Mail, 
  Clock, 
  Shield, 
  Star, 
  Users, 
  Sparkles,
  CheckCircle2
} from 'lucide-react';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Use 'instant' behavior to override CSS smooth scroll and ensure immediate scroll to top
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
};

const LOGO_WHITE_BG = "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/zydgwvt1_13cabsbook%20logo%20short.png";

const HERO_BG = "https://images.unsplash.com/photo-1552912809-9e8ea7ddc3d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

// Updated vehicle images - User provided taxi photos
const FLEET_IMAGES = {
  sedan: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/q4ct6q3p_image.png",
  premium_sedan: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/24mvvbgz_image.png",
  suv: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/pov05qzs_image.png",
  premium_suv: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/kh7gk9ht_lexus-suv-1.jpg",
  maxi: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/8ly5dt5z_image.png",
};

// Hero Section
const HeroSection = () => (
  <section 
    id="booking"
    className="relative min-h-screen pt-24 flex items-center hero-section"
    style={{
      backgroundImage: `linear-gradient(to bottom, rgba(26, 26, 26, 0.75), rgba(26, 26, 26, 0.85)), url(${HERO_BG})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center bottom',
    }}
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-20 w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Left Content */}
        <div className="text-white space-y-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="inline-block px-4 py-1 bg-primary/20 border border-primary/30 rounded-full">
            <span className="text-primary font-medium text-sm">Melbourne's Trusted Taxi Service</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter uppercase leading-tight">
            Your Ride,<br />
            <span className="text-primary">Your Way</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-lg leading-relaxed">
            Premium taxi service across Victoria. Book online 24/7 with instant confirmation. 
            Professional drivers, competitive rates, no hidden costs.
          </p>
          <div className="flex flex-wrap gap-4 pt-2">
            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>24/7 Service</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Fixed Prices</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Safe & Reliable</span>
            </div>
          </div>
        </div>

        {/* Right - Booking Form */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <BookingForm />
        </div>
      </div>
    </div>
  </section>
);

// Why Choose Us Section
const WhyChooseUs = () => {
  const features = [
    {
      icon: <Clock className="w-8 h-8" />,
      title: "24/7 Availability",
      description: "Book a taxi anytime, day or night. We're always ready to serve you."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Safe & Secure",
      description: "All vehicles are regularly inspected. Drivers are police-checked and trained."
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "5-Star Service",
      description: "Professional, courteous drivers committed to your comfort and satisfaction."
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Victoria Wide",
      description: "Covering Melbourne CBD, suburbs, airports, and regional Victoria."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Clean Vehicles",
      description: "Our fleet is maintained to the highest standards of cleanliness and hygiene."
    },
    {
      icon: <CheckCircle2 className="w-8 h-8" />,
      title: "Group Travel",
      description: "Maxi taxis available for groups up to 11 passengers with luggage space."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="why-choose-us">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Why Choose Us</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-secondary mt-2 tracking-tight">
            Melbourne's Premier Taxi Service
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group p-6 md:p-8 rounded-xl border border-gray-100 bg-white hover:border-primary hover:shadow-lg transition-all duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-14 h-14 rounded-lg bg-orange-50 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                {feature.icon}
              </div>
              <h3 className="font-heading text-xl font-bold text-secondary mb-2">{feature.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Fleet Section
const FleetSection = () => {
  const vehicles = [
    {
      name: "Sedan Taxi",
      image: FLEET_IMAGES.sedan,
      passengers: "1-4",
      description: "Comfortable standard taxi for everyday travel",
      features: ["Affordable", "Quick pickup"]
    },
    {
      name: "Premium Sedan",
      image: FLEET_IMAGES.premium_sedan,
      passengers: "1-4",
      description: "Mercedes for business and special occasions",
      features: ["Executive service", "Luxury comfort"]
    },
    {
      name: "SUV Taxi",
      image: FLEET_IMAGES.suv,
      passengers: "1-6",
      description: "Extra space for passengers and luggage",
      features: ["More legroom", "Large boot"]
    },
    {
      name: "Premium SUV",
      image: FLEET_IMAGES.premium_suv,
      passengers: "1-6",
      description: "Lexus SUV for luxury group travel",
      features: ["Premium comfort", "Executive style"]
    },
    {
      name: "Maxi Taxi",
      image: FLEET_IMAGES.maxi,
      passengers: "1-11",
      description: "Perfect for groups and events",
      features: ["Group travel", "Event transport"]
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Our Fleet</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-secondary mt-2 tracking-tight">
            Choose Your Vehicle
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            From standard taxis to premium sedans and spacious maxis - we have the perfect vehicle for every journey.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {vehicles.map((vehicle, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-primary transition-all duration-300 hover:shadow-xl"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={vehicle.image} 
                  alt={vehicle.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-lg font-bold text-secondary">{vehicle.name}</h3>
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    {vehicle.passengers}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{vehicle.description}</p>
                <div className="flex flex-wrap gap-2">
                  {vehicle.features.map((feature, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-orange-50 text-primary rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Areas Section
const ServiceAreas = () => (
  <section className="py-16 md:py-20 bg-secondary text-white">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Coverage</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mt-2 tracking-tight">
            Serving All of Victoria
          </h2>
          <p className="text-gray-400 mt-4 leading-relaxed">
            From Melbourne CBD to the furthest suburbs and beyond - we've got Victoria covered. 
            Airport transfers, city rides, regional trips - book with confidence.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-8">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Melbourne CBD</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Melbourne Airport</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Avalon Airport</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>All Suburbs</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Geelong</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Ballarat</span>
            </div>
          </div>
        </div>
        <div className="bg-secondary-foreground/5 rounded-2xl p-8 border border-white/10">
          <h3 className="font-heading text-2xl font-bold mb-6">Quick Contact</h3>
          <div className="space-y-4">
            <a 
              href="tel:1300096685" 
              className="flex items-center gap-4 p-4 bg-white/5 rounded-lg hover:bg-primary/20 transition-colors duration-200"
              data-testid="contact-phone"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Call Us Anytime</p>
                <p className="text-lg font-semibold">1300 096 685</p>
              </div>
            </a>
            <a 
              href="mailto:13cabsbook.com.au@gmail.com" 
              className="flex items-center gap-4 p-4 bg-white/5 rounded-lg hover:bg-primary/20 transition-colors duration-200"
              data-testid="contact-email"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-400">Email Us</p>
                <p className="text-sm sm:text-lg font-semibold break-all">13cabsbook.com.au@gmail.com</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Home Page
const Home = () => {
  return (
    <div data-testid="home-page">
      <Header />
      <HeroSection />
      <WhyChooseUs />
      <FleetSection />
      <ServiceAreas />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        {/* Scroll to top on route change */}
        <ScrollToTop />
        {/* SEO meta tags and schema markup */}
        <SEOHead />
        {/* Load tracking scripts on all pages */}
        <TrackingScripts />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route path="/airport-transfers" element={<AirportTransfersPage />} />
          <Route path="/corporate" element={<CorporatePage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
